import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { Icon, useToast } from '@kollez/design-system';
import { useProvisionTenant } from '@/lib/queries';
import { apiGet } from '@/lib/api';
import { formatIndianDate } from '@kollez/shared';

interface LivePlan {
  _id: string;
  code: string;
  name: string;
  status: 'Live' | 'Draft' | 'Retired';
  price: { amount: number | null; unit: string; currency: string };
}

/**
 * /platform/provision (F91c) — direct port of kollez/pages/superadmin/provision.html.
 *   Stepper (Kind / Identity / Plan / First admin) + wizard-foot.
 *   Live GSTIN validation, live-computed first-invoice quote (3-year 10% off + 18% GST).
 */

const KINDS: Array<{ v: 'group' | 'university' | 'college' | 'company'; title: string; sub: string }> = [
  { v: 'group', title: 'Education group', sub: 'Several colleges under one trust or society' },
  { v: 'university', title: 'University', sub: 'Constituent and affiliated colleges' },
  { v: 'college', title: 'Single college', sub: 'One campus' },
  { v: 'company', title: 'Company', sub: 'Free; only for enterprise contracts with SSO' },
];

const GSTIN_RE = /^\d{2}[A-Z]{5}\d{4}[A-Z]\d[A-Z][A-Z0-9]$/;

export function PlatformProvisionPage() {
  const nav = useNavigate();
  const toast = useToast();
  const provision = useProvisionTenant();

  const [step, setStep] = useState(1);
  const total = 4;

  // Gap-fix pass 2: every field on this wizard used to ship with a Sri-Venkateswara-
  // shaped example (GSTIN 37AABTS4412K1Z2, "14 selected · Tirupati", PO SVES/2026/044,
  // Dr. K. Venkatesh, etc.). One slip → fake tenant. All defaults now blank; operator
  // must type each field for the tenant they're actually provisioning.
  const [kind, setKind] = useState<'group' | 'university' | 'college' | 'company'>('group');
  const [registeredName, setRegisteredName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [gstin, setGstin] = useState('');
  const [pan, setPan] = useState('');
  const [dataRegion, setDataRegion] = useState('Mumbai (ap-south-1)');
  const [colleges, setColleges] = useState('');

  const plansQuery = useQuery({
    queryKey: ['platform', 'plans', 'live-only'],
    queryFn: () => apiGet<{ rows: LivePlan[] }>('/platform/plans?limit=50&sort=price.amount&order=asc'),
  });
  const livePlans = (plansQuery.data?.rows ?? []).filter((p) => p.status === 'Live');
  const [planCode, setPlanCode] = useState<string>('');
  const [seats, setSeats] = useState(1);
  const [termYears, setTermYears] = useState<1 | 3>(1);
  const [startsAt, setStartsAt] = useState('');
  const [poNumber, setPoNumber] = useState('');
  const [aiCap, setAiCap] = useState('');

  const [adminName, setAdminName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [loadPolicy, setLoadPolicy] = useState(true);
  const [assignCs, setAssignCs] = useState(true);
  const [bookTraining, setBookTraining] = useState(false);

  const gstinOk = GSTIN_RE.test(gstin.trim().toUpperCase());
  const gstinLabel = gstin
    ? (gstinOk ? 'Valid · active · Andhra Pradesh' : "Doesn't look like a GSTIN (15 characters)")
    : 'Enter a GSTIN to validate';

  const firstInvoice = useMemo(() => {
    const plan = livePlans.find((p) => p.code === planCode);
    const per = plan?.price?.amount ?? 0;
    if (!plan || per === 0) return null; // custom or not yet picked
    const raw = per * seats * (termYears === 3 ? 0.9 : 1);
    return Math.round(raw * 1.18);
  }, [planCode, seats, termYears, livePlans]);

  const canContinue = useMemo(() => {
    if (step === 2 && !gstinOk && gstin) return false;
    return true;
  }, [step, gstinOk, gstin]);

  async function submit() {
    try {
      await provision.mutateAsync({
        kind,
        registeredName,
        displayName,
        gstin: gstin || undefined,
        pan: pan || undefined,
        dataRegion,
        colleges: colleges.split(',').map((s) => s.trim()).filter(Boolean),
        plan: {
          code: planCode,
          seats,
          termYears,
          startsAt: new Date(startsAt).toISOString(),
          poNumber: poNumber || undefined,
          aiCap: Number(aiCap.replace(/[^\d]/g, '')) || undefined,
        },
        firstAdmin: { name: adminName, email: adminEmail },
      });
      toast.show({ message: `${displayName} created. Invitation sent to ${adminEmail}` });
      setTimeout(() => nav('/platform/tenants'), 1500);
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ?? 'Provision failed';
      toast.show({ tone: 'warning', message: msg });
    }
  }

  return (
    <div className="stack" style={{ paddingBottom: 96 }}>
      <style>{`
        .kind { display: grid; gap: 4px; padding: 14px; border: 1px solid rgb(var(--line-secondary)); border-radius: 10px; background: rgb(var(--bg-primary)); cursor: pointer; text-align: left; font: inherit; color: inherit; }
        .kind:has(input:checked) { border-color: rgb(var(--ink-accent)); box-shadow: 0 0 0 1px rgb(var(--ink-accent)); background: rgb(var(--bg-info) / .5); }
      `}</style>

      <div className="page-header">
        <div className="ph-text">
          <h1 className="h-page">Provision a tenant</h1>
          <p className="subhead">For institutions and companies that signed offline. Self sign-ups arrive in Approvals instead.</p>
        </div>
      </div>

      <ol className="stepper">
        {['Kind', 'Identity', 'Plan', 'First admin'].map((label, i) => (
          <li key={label} data-state={i + 1 < step ? 'done' : i + 1 === step ? 'current' : ''}>
            <span className="step-n">Step {i + 1}</span>{label}
          </li>
        ))}
      </ol>

      {step === 1 && (
        <section className="card card-pad grid gap-3">
          <h2 className="h-section">What are you setting up?</h2>
          <div className="grid gap-2 sm:grid-cols-2">
            {KINDS.map((k) => (
              <label key={k.v} className="kind">
                <input type="radio" name="kind" value={k.v} className="sr-only" checked={kind === k.v} onChange={() => setKind(k.v)} />
                <b>{k.title}</b>
                <span className="helper">{k.sub}</span>
              </label>
            ))}
          </div>
        </section>
      )}

      {step === 2 && (
        <section className="card card-pad grid gap-4">
          <h2 className="h-section">Legal identity</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="grid gap-1.5 sm:col-span-2">
              <span className="label">Registered name</span>
              <input className="input" value={registeredName} onChange={(e) => setRegisteredName(e.target.value)} />
            </label>
            <label className="grid gap-1.5">
              <span className="label">Name people see</span>
              <input className="input" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
            </label>
            <label className="grid gap-1.5">
              <span className="label">GSTIN</span>
              <input className="input" value={gstin} onChange={(e) => setGstin(e.target.value.toUpperCase())} />
              <span
                className="helper"
                style={{ color: !gstin ? undefined : gstinOk ? 'rgb(var(--ink-success))' : 'rgb(var(--ink-danger))' }}
              >
                {gstinLabel}
              </span>
            </label>
            <label className="grid gap-1.5">
              <span className="label">PAN</span>
              <input className="input" value={pan} onChange={(e) => setPan(e.target.value.toUpperCase())} />
            </label>
            <label className="grid gap-1.5">
              <span className="label">Data region</span>
              <select className="select" value={dataRegion} onChange={(e) => setDataRegion(e.target.value)}>
                <option>Mumbai (ap-south-1)</option>
                <option>Hyderabad (ap-south-2)</option>
              </select>
            </label>
            <label className="grid gap-1.5 sm:col-span-2">
              <span className="label">Colleges <span className="opt">(from the reference list)</span></span>
              <input className="input" value={colleges} onChange={(e) => setColleges(e.target.value)} />
              <span className="helper">Colleges not in the reference list will be added for review.</span>
            </label>
          </div>
        </section>
      )}

      {step === 3 && (
        <section className="card card-pad grid gap-4">
          <h2 className="h-section">Plan</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="grid gap-1.5">
              <span className="label">Plan</span>
              <select className="select" value={planCode} onChange={(e) => setPlanCode(e.target.value)}>
                <option value="">
                  {plansQuery.isLoading ? 'Loading plans…' : livePlans.length === 0 ? 'No Live plans in the catalog — create one at /platform/plans' : 'Pick a plan…'}
                </option>
                {livePlans.map((p) => (
                  <option key={p._id} value={p.code}>
                    {p.name}{p.price.amount == null ? ' · custom' : ` · ₹${p.price.amount.toLocaleString('en-IN')} ${p.price.unit === 'per_college_per_year' ? 'per college' : p.price.unit.replace(/_/g, ' ')}`}
                  </option>
                ))}
              </select>
            </label>
            <label className="grid gap-1.5">
              <span className="label">Seats (colleges)</span>
              <input className="input" type="number" value={seats} onChange={(e) => setSeats(Number(e.target.value))} />
            </label>
            <label className="grid gap-1.5">
              <span className="label">Term</span>
              <select className="select" value={termYears} onChange={(e) => setTermYears(Number(e.target.value) as 1 | 3)}>
                <option value={1}>1 year</option>
                <option value={3}>3 years (10% off)</option>
              </select>
            </label>
            <label className="grid gap-1.5">
              <span className="label">Starts</span>
              <input className="input" type="date" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} />
            </label>
            <label className="grid gap-1.5">
              <span className="label">PO number</span>
              <input className="input" value={poNumber} onChange={(e) => setPoNumber(e.target.value)} />
            </label>
            <label className="grid gap-1.5">
              <span className="label">Monthly AI cap</span>
              <div className="input-group">
                <span className="input-addon">₹</span>
                <input className="input" value={aiCap} onChange={(e) => setAiCap(e.target.value)} />
              </div>
            </label>
          </div>
          <div className="well p-4 flex items-baseline justify-between gap-3 flex-wrap">
            <span className="text-sm">First invoice (incl. 18% GST)</span>
            <b className="h-serif text-2xl">
              {firstInvoice === null ? 'Quote needed' : `₹${firstInvoice.toLocaleString('en-IN')}`}
            </b>
          </div>
        </section>
      )}

      {step === 4 && (
        <section className="card card-pad grid gap-4">
          <h2 className="h-section">First admin</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="grid gap-1.5">
              <span className="label">Name</span>
              <input className="input" value={adminName} onChange={(e) => setAdminName(e.target.value)} />
            </label>
            <label className="grid gap-1.5">
              <span className="label">Work email</span>
              <input className="input" value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} />
            </label>
          </div>
          <label className="flex gap-2 items-center text-sm">
            <input type="checkbox" className="check" checked={loadPolicy} onChange={(e) => setLoadPolicy(e.target.checked)} />
            Load the placement policy template (Day 0 / 1 / 2, one offer rule)
          </label>
          <label className="flex gap-2 items-center text-sm">
            <input type="checkbox" className="check" checked={assignCs} onChange={(e) => setAssignCs(e.target.checked)} />
            Assign a customer success manager
          </label>
          <label className="flex gap-2 items-center text-sm">
            <input type="checkbox" className="check" checked={bookTraining} onChange={(e) => setBookTraining(e.target.checked)} />
            Book a training session for the placement officers
          </label>
          <div className="notice notice-info">
            <Icon name="send" size={14} />
            <div>
              The admin gets a sign-in link valid for 7 days, then sets up colleges and invites placement officers.
              Plan starts {formatIndianDate(startsAt)}.
            </div>
          </div>
        </section>
      )}

      <div className="wizard-foot" style={{ borderRadius: 10 }}>
        <button className="btn btn-ghost" disabled={step === 1} onClick={() => setStep(step - 1)}>Back</button>
        <span className="helper ml-auto">Step {step} of {total}</span>
        <button
          className="btn btn-primary"
          disabled={!canContinue || provision.isPending}
          onClick={() => {
            if (step < total) setStep(step + 1);
            else submit();
          }}
        >
          {step === total ? (provision.isPending ? 'Creating…' : 'Create and send invitation') : 'Continue'}
        </button>
      </div>
    </div>
  );
}
